import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { TableEmptyState } from '@/components/data/TableEmptyState'
import { IconButton } from '@/components/ui/IconButton'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/cn'

/* The dense table is the most important component in the product, so its rules
 * are ABSOLUTE (DESIGN.md § Components):
 *
 *   · No vertical rules, ever — columns are held by alignment.
 *   · The header is uppercase eyebrow text on WHITE, sticky, with a single
 *     bottom border. It is never a filled grey bar.
 *   · No border on every cell. No shadow at any time, including on hover.
 *
 * Those three together are the rejected spreadsheet aesthetic, and
 * reintroducing any ONE of them brings it back.
 *
 * Rows carry three levels of hierarchy: the primary value at regular weight on
 * ink, secondary detail at 12px on muted, and record identifiers at emphasis
 * weight in brand. The trailing column is reserved for flags and stays empty on
 * healthy rows — empty space in a healthy row is what makes an unhealthy row
 * visible.
 *
 * TanStack Table is headless and contributes no visual language of its own,
 * which is exactly why it was chosen over AG Grid.
 *
 * ── Virtualisation (UX-DR-4) ──────────────────────────────────────────────
 * Above VIRTUALISE_ABOVE rows the body is windowed with @tanstack/react-virtual
 * — same family as the react-query and react-table this client already runs,
 * which is the argument for it over a second vendor's mental model inside a
 * component that has to interleave with react-table's row model.
 *
 * AT OR BELOW THAT COUNT NOTHING CHANGES, node for node. That is not a
 * micro-optimisation: below a hundred rows the windowing costs more than it
 * saves, and a row that exists but is not rendered is indistinguishable, from
 * a test's or a Ctrl-F's point of view, from a row that is missing. Every list
 * in the product today is under the threshold, so every existing assertion
 * that reaches a row by its text still reaches it.
 *
 * The window is a scrolling container plus two SPACER ROWS carrying the
 * measured offsets, rather than absolutely-positioned divs with ARIA grid
 * roles. The table stays a real `<table>` with a real `<thead>` and scoped
 * headers (UX-DR-25, table-semantics.test.ts) and the eyebrow header stays
 * sticky against the new scroll container. A windowed table also has to say
 * how many rows there really are, which is what aria-rowcount/aria-rowindex
 * are for — without them a screen reader announces "row 4 of 40" on a list of
 * four hundred.
 *
 * The virtualiser costs this component NO React-Compiler memoisation, and that
 * is worth stating because the lint warning looks like it does: the compiler
 * already skips DataTable on `useReactTable` below (react-hooks/incompatible-
 * library, and it did so before any of this existed — both TanStack hooks
 * return functions that cannot be memoised safely). Splitting the window into a
 * child would move a bail-out that is already unconditional here, so the body
 * stays in one piece. On the board next door the same hook DOES sit in its own
 * child (RecordBoard's LaneCards), where it confines the skip to the lane.
 *
 * ── Paging (opt-in) ───────────────────────────────────────────────────────
 * A table pages only when the caller passes `pagination`, and nothing about a
 * table that does not is altered — no row model, no footer, no extra node.
 * Paging and windowing solve different problems and are not alternatives: the
 * window is about what the browser has to draw, the pager is about giving the
 * reader a countable list with a floor under it ("6 quotes") and a say in how
 * much of it arrives at once. A paged table's window simply never engages,
 * because a page is smaller than the threshold.
 *
 * ── The empty table (UX-DR-17) ────────────────────────────────────────────
 * A table with no rows is two different facts wearing one face, and the table
 * renders the distinction itself rather than leaving five callers to each
 * answer it: see TableEmptyState. A caller that passes neither `empty` nor
 * `filtered` still gets an honest *nothing yet* state, so the way to get this
 * wrong is now to opt into it.
 */

/** Column meta the table reads for alignment and hierarchy. */
export interface AsaColumnMeta {
  /** Right-align with tabular figures. Use for every numeric column. */
  numeric?: boolean
  /** The trailing flag column — narrow, empty on healthy rows. */
  flag?: boolean
  headerClassName?: string
  cellClassName?: string
}

declare module '@tanstack/react-table' {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  interface ColumnMeta<TData, TValue> extends AsaColumnMeta {
    // TData and TValue are fixed by the module's own declaration and are unused
    // here; the augmentation exists purely to add the ASA keys above.
    _asa?: never
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */
}

export interface DataTableProps<TData> {
  data: TData[]
  // Columns come from `createColumnHelper`, whose accessors each carry their
  // own value type, so the array is heterogeneous by construction. TanStack's
  // own signature uses `any` here for exactly that reason; a narrower type
  // rejects a legitimate mixed-accessor column list.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  onRowClick?: (row: TData) => void
  /** Rendered in place of rows when the dataset is genuinely empty.
   *
   *  Left off, the table says so itself through TableEmptyState. It is IGNORED
   *  when `filtered` is set, because a bespoke "no invoices yet" node is a
   *  statement about the dataset and would be a lie about a filter — see
   *  `filtered` below. */
  empty?: ReactNode
  /** True when a filter narrowed a NON-EMPTY dataset to nothing (UX-DR-17).
   *
   *  Only the caller can know this: from here an empty response and a
   *  filtered-away one are the same array. Setting it swaps the *nothing yet*
   *  message for *nothing matches* and, with `onClearFilters`, the way back —
   *  the difference between "we are owed nothing" and "you are looking at
   *  March". */
  filtered?: boolean
  /** Clears every filter on the surface. */
  onClearFilters?: () => void
  /** Comfortable is for decision surfaces; dense for entering and verifying rows. */
  density?: 'dense' | 'comfortable'
  className?: string
  /** Accessible name for the grid. */
  label: string
  /* Sorting and column visibility are CONTROLLED only if the caller passes
     them. Left off, the table keeps its own state exactly as it always has, so
     no existing caller changes behaviour by this becoming possible. A surface
     that persists them per user (useViewState) passes both halves. */
  sorting?: SortingState
  /** Receives the resolved next state, never an updater — the persisted
   *  setters take a value, and resolving the updater here keeps every caller
   *  from having to know that TanStack passes one. */
  onSortingChange?: (next: SortingState) => void
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (next: VisibilityState) => void
  /** Pages the table and gives it a footer. Absent — every table in the
   *  product until now — nothing is added: no row model, no footer, no extra
   *  node, and the DOM is what it was.
   *
   *  A table is paged only where the reader has a reason to STOP at a page
   *  boundary. Windowing (see above) already answers the performance half, and
   *  answers it better: a scrollbar shows how much list there is, while a pager
   *  hides it behind a number. What a pager adds is a floor under the surface —
   *  a countable "6 quotes" and a place to say how many of them fit at once. */
  pagination?: TablePagination
}

export interface TablePagination {
  /** Rows per page. */
  pageSize: number
  onPageSizeChange: (next: number) => void
  /** The sizes the footer offers. */
  pageSizeOptions: readonly number[]
  /** The count sentence at the left of the footer — "6 quotes". Domain wording,
   *  so the table asks for it rather than inventing "6 rows": a row is what the
   *  reader is looking AT, not what they came to count. */
  summary?: (total: number) => ReactNode
}

/** Above this many rows the body is windowed. See the note at the top of the
 *  file for why this is a threshold rather than a default. */
export const VIRTUALISE_ABOVE = 100

/** Matches --spacing-row-dense / --spacing-row-comfortable in tokens.css. The
 *  virtualiser needs a number and CSS variables are not readable from here, so
 *  the two are kept in step by hand; a wrong value costs a little scroll drift,
 *  not a broken window, because the rows are all exactly this tall. */
const ROW_HEIGHT_PX: Record<'dense' | 'comfortable', number> = {
  dense: 38,
  comfortable: 44,
}

export function DataTable<TData>({
  data,
  columns,
  onRowClick,
  empty,
  filtered = false,
  onClearFilters,
  density = 'dense',
  className,
  label,
  sorting: sortingProp,
  onSortingChange,
  columnVisibility: visibilityProp,
  onColumnVisibilityChange,
  pagination,
}: DataTableProps<TData>) {
  const { t } = useTranslation()
  const [internalSorting, setInternalSorting] = useState<SortingState>([])
  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>({})
  /* The page INDEX is the table's own, always. Unlike sorting and visibility it
     is not a habit worth remembering: coming back to a list on page four of a
     set that has since changed underneath you is a way to lose a record, not to
     find one. The page SIZE is the caller's, and that one is remembered. */
  const [pageIndex, setPageIndex] = useState(0)

  const sorting = sortingProp ?? internalSorting
  const columnVisibility = visibilityProp ?? internalVisibility

  const paged = pagination !== undefined
  const pageSize = pagination?.pageSize ?? 0

  /* Built once per mount rather than per render: the factory closes over a
     memo cache, and a fresh one each render throws that cache away and re-pages
     the list on every keystroke elsewhere on the screen. Undefined when the
     caller did not ask for paging, which is what leaves every other table's row
     model untouched. */
  const paginationRowModel = useMemo(
    () => (paged ? getPaginationRowModel<TData>() : undefined),
    [paged],
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      ...(paged ? { pagination: { pageIndex, pageSize } } : {}),
    },
    getPaginationRowModel: paginationRowModel,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater
      setPageIndex(next.pageIndex)
      if (next.pageSize !== pageSize) pagination?.onPageSizeChange(next.pageSize)
    },
    /* TanStack hands back either a value or an updater. Both are resolved here
       so the callback prop always receives the finished state, and the internal
       copy is only written when the caller is NOT controlling that half —
       otherwise an uncontrolled shadow would drift behind the controlled one
       and win the next time the caller stopped passing the prop. */
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      if (!sortingProp) setInternalSorting(next)
      onSortingChange?.(next)
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater
      if (!visibilityProp) setInternalVisibility(next)
      onColumnVisibilityChange?.(next)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const rowHeight =
    density === 'dense'
      ? 'h-[var(--spacing-row-dense)]'
      : 'h-[var(--spacing-row-comfortable)]'

  const rows = table.getRowModel().rows
  const columnCount = table.getVisibleLeafColumns().length

  /* Every row the pager is paging over, which is the figure the footer counts
     and the one the reader means by "6 quotes" — `rows` above is one page of
     them once paging is on. */
  const total = paged ? table.getPrePaginationRowModel().rows.length : rows.length
  const pageCount = paged ? Math.max(table.getPageCount(), 1) : 1

  /* A page that stopped existing — a filter applied while the reader was on
     page four — sends them back to the first rather than to a blank table with
     working arrows. Corrected DURING RENDER, the pattern useViewState documents:
     an effect would paint the empty page first, and an empty table is read as
     "nothing here" rather than as "wrong page". */
  if (paged && pageIndex > 0 && pageIndex >= pageCount) setPageIndex(0)

  const virtualise = rows.length > VIRTUALISE_ABOVE
  const scrollRef = useRef<HTMLDivElement>(null)

  /* `enabled` rather than a conditional hook: hooks cannot be called on a
     condition, and disabled the virtualiser measures nothing and returns no
     items, which is precisely the "render what we render today" branch. */
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX[density],
    // Rows are exactly one token tall, so the estimate is the measurement and
    // nothing needs re-measuring after paint.
    overscan: 12,
    enabled: virtualise,
  })

  const windowed = virtualise ? virtualizer.getVirtualItems() : []
  const first = windowed[0]
  const last = windowed[windowed.length - 1]
  const padTop = first ? first.start : 0
  const padBottom = last ? virtualizer.getTotalSize() - last.end : 0

  /* The rows that actually reach the DOM, paired with the index they hold in
     the full list — the index is what aria-rowindex announces, and it is not
     the position in this array once a window has been cut out of the middle. */
  const rendered: { row: (typeof rows)[number]; index: number }[] = virtualise
    ? windowed.flatMap((item) => {
        const row = rows[item.index]
        return row ? [{ row, index: item.index }] : []
      })
    : rows.map((row, index) => ({ row, index }))

  /* The pager itself. Numbered buttons while they fit, because a list of six
     pages is a place the reader jumps around in, and an arrow-only pager makes
     page five four clicks away. Past that the numbers stop being a map and
     become a wall, so they collapse to the position — which is the only part of
     a forty-page pager anyone reads. */
  const numbered = pageCount <= 7
  const pages = numbered ? Array.from({ length: pageCount }, (_, index) => index) : []

  /* No footer over an empty table. The empty state already says what happened;
     a pager under it offers to page through nothing and a count under it says
     "0 quotes" twice. */
  const footer = paged && total > 0 ? (
    <div
      className={cn(
        'border-line-hair flex flex-wrap items-center justify-between gap-x-4 gap-y-2',
        'border-t px-3 py-2.5',
      )}
    >
      <span className="text-text-muted text-[12.5px] leading-none font-light">
        {pagination?.summary?.(total)}
      </span>

      {/* The two controls sit together at the trailing edge: how much fits, and
          where in it you are. They are one question asked twice. */}
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-2">
          {/* The wording is on the Select as well (aria-label), so this line is
              the sighted reader's copy of the same label and may go when the
              footer runs out of room. */}
          <span
            aria-hidden
            className="text-text-muted hidden text-[12.5px] leading-none font-light whitespace-nowrap sm:inline"
          >
            {t('common.pagination.rowsPerPage')}
          </span>
          <Select
            selectSize="sm"
            aria-label={t('common.pagination.rowsPerPage')}
            value={String(pageSize)}
            onChange={(next) => table.setPageSize(Number(next))}
            options={(pagination?.pageSizeOptions ?? []).map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </span>

        <nav
          aria-label={t('common.pagination.label')}
          className="flex items-center gap-1"
        >
          <IconButton
            size="sm"
            icon={ChevronLeft}
            label={t('common.pagination.previous')}
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          />
          {numbered ? (
            pages.map((index) => {
              const current = index === pageIndex
              return (
                <button
                  key={index}
                  type="button"
                  // The current page is a state, not a label: `aria-current`
                  // is what says so to a screen reader, since the ring and the
                  // colour say it to nobody else.
                  aria-current={current ? 'page' : undefined}
                  aria-label={t('common.pagination.page', { page: index + 1 })}
                  onClick={() => table.setPageIndex(index)}
                  className={cn(
                    'asa-num h-7 min-w-7 cursor-pointer rounded-md border px-2',
                    'text-[12.5px] leading-none transition-colors',
                    'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
                    current
                      ? 'border-brand text-brand'
                      : 'text-text-muted hover:bg-surface-hover border-transparent',
                  )}
                >
                  {index + 1}
                </button>
              )
            })
          ) : (
            <span className="asa-num text-text-muted px-1 text-[12.5px] leading-none">
              {t('common.pagination.position', {
                page: pageIndex + 1,
                total: pageCount,
              })}
            </span>
          )}
          <IconButton
            size="sm"
            icon={ChevronRight}
            label={t('common.pagination.next')}
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          />
        </nav>
      </div>
    </div>
  ) : null

  return (
    <>
    <div
      ref={scrollRef}
      className={cn(
        'asa-scroll w-full overflow-x-auto',
        // A window needs a viewport to be a window. The height cap only exists
        // above the threshold, so a short list keeps growing with the page the
        // way it always did rather than gaining a scrollbar it never had.
        virtualise && 'max-h-[70vh] overflow-y-auto',
        className,
      )}
    >
      <table
        className="w-full border-collapse text-left"
        aria-label={label}
        // +1 for the header row, which is a row as far as ARIA is concerned.
        aria-rowcount={virtualise ? rows.length + 1 : undefined}
      >
        <thead>
          <tr className="border-line border-b" aria-rowindex={virtualise ? 1 : undefined}>
            {table.getFlatHeaders().map((header) => {
              const meta = header.column.columnDef.meta
              const sortable = header.column.getCanSort()
              const sorted = header.column.getIsSorted()

              return (
                <th
                  key={header.id}
                  scope="col"
                  aria-sort={
                    sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : undefined
                  }
                  style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}
                  className={cn(
                    // Uppercase eyebrow on white — never a filled grey bar.
                    'bg-surface asa-eyebrow sticky top-0 z-10 h-9 px-3 whitespace-nowrap',
                    meta?.numeric && 'text-right',
                    meta?.flag && 'w-10',
                    meta?.headerClassName,
                  )}
                >
                  {header.isPlaceholder ? null : sortable ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        'inline-flex cursor-pointer items-center gap-1 uppercase',
                        'hover:text-text-heading transition-colors',
                        meta?.numeric && 'flex-row-reverse',
                      )}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sorted === 'asc' ? (
                        <ArrowUp size={11} aria-hidden />
                      ) : sorted === 'desc' ? (
                        <ArrowDown size={11} aria-hidden />
                      ) : null}
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="p-0">
                {/* The filtered branch WINS over a caller's own node. A
                    bespoke `empty` says something about the dataset ("no
                    invoices yet"), and rendering it while a filter is holding
                    rows back is the exact conflation UX-DR-17 forbids — the
                    founder reads "no receivables" and concludes the company is
                    owed nothing, when they are looking at March. */}
                {!filtered && empty ? (
                  empty
                ) : (
                  <TableEmptyState filtered={filtered} onClearFilters={onClearFilters} />
                )}
              </td>
            </tr>
          ) : (
            <>
              {/* The two spacers stand in for the rows outside the window and
                  carry their measured height, so the scrollbar reports the
                  real length of the list and the rows below the fold sit where
                  the scroll position says they are. Both collapse to nothing
                  under the threshold, which is what makes an unvirtualised
                  table byte-identical to the one this file rendered before.
                  aria-hidden because a spacer is a scrollbar artefact, not a
                  row — aria-rowcount above is what tells the truth about how
                  many there are. */}
              {padTop > 0 ? (
                <tr aria-hidden>
                  <td colSpan={columnCount} className="p-0" style={{ height: padTop }} />
                </tr>
              ) : null}

              {rendered.map(({ row, index }) => (
                <tr
                  key={row.id}
                  // 1-based and counting the header row, so the first data row
                  // is 2. Only present when windowing, since an unwindowed
                  // table's DOM order already is its row order.
                  aria-rowindex={virtualise ? index + 2 : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    // Row separators only — no vertical rules, no cell borders.
                    'border-line-hair border-b transition-colors',
                    'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
                    rowHeight,
                    onRowClick && 'hover:bg-surface-hover cursor-pointer',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          'text-text-body px-3 text-[13px] leading-[1.45] font-normal',
                          meta?.numeric && 'asa-num text-right',
                          meta?.cellClassName,
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              ))}

              {padBottom > 0 ? (
                <tr aria-hidden>
                  <td
                    colSpan={columnCount}
                    className="p-0"
                    style={{ height: padBottom }}
                  />
                </tr>
              ) : null}
            </>
          )}
        </tbody>
      </table>
    </div>
      {footer}
    </>
  )
}

/* ── Cell helpers ─────────────────────────────────────────────────────────── */

/** A record identifier: emphasis weight in brand. One of the four sanctioned
 *  uses of weight 600. */
export function RecordId({ children }: { children: ReactNode }) {
  return (
    <span className="text-brand text-[13px] leading-[1.45] font-semibold">
      {children}
    </span>
  )
}

/** The primary value of a row plus its secondary line. The primary stays at
 *  REGULAR weight — it separates from the secondary by weight drop and colour,
 *  not by going bolder. If every row's main cell were emphasised, the emphasis
 *  would stop meaning anything. */
export function RowValue({
  primary,
  secondary,
}: {
  primary: ReactNode
  secondary?: ReactNode
}) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-text-heading truncate text-[13px] leading-[1.45] font-normal">
        {primary}
      </span>
      {secondary ? (
        <span className="text-text-muted truncate text-[12px] leading-[1.4] font-light">
          {secondary}
        </span>
      ) : null}
    </span>
  )
}
